import React, { useEffect } from "react";
import { TextArea } from "../../../ui/textarea";
import { Label } from "../../../ui/label";
import type { FieldControlSize } from "../../../../lib/field-control-size";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../ui/tooltip";
import { useSelector } from "react-redux";
import { errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { setErrorMsg } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { store } from "../../../../store";

interface TextAreaFieldProps {
  label: string;
  name: string;
  value: string | number;
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  placeholder?: string;
  pattern?: RegExp;
  patternMsg?: string;
  tooltipText?: React.ReactNode;
  onChange: (name: string, value: string) => void;
  onBlur?: (e: any) => void;
  handleDisabled?: (name: string, defaultValue: any) => void;
  defaultValue?: any;
  autoFocus?: boolean;
  onKeyDown?: (e: any) => void;
  controlSize?: FieldControlSize;
}

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  label,
  name,
  value,
  required = false,
  disabled = false,
  maxLength,
  placeholder,
  pattern,
  patternMsg,
  tooltipText,
  onChange,
  onBlur,
  handleDisabled,
  defaultValue,
  autoFocus,    
  onKeyDown,
  controlSize,
}) => {
  const dispatch = useStoreDispatch();
  const errorMsg = useSelector(errorMsgSelector);
  const error = errorMsg?.[name] || "";
  const TEXT_SANITIZE_RE = /(<([^>]+)>)|<>/gi;

  /* Reset to default when disabled */
  useEffect(() => {
    if (handleDisabled && disabled) {
      handleDisabled(name, defaultValue);
    }
  }, [disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- inline validation ---- */
  const validate = (raw: string) => {
    const existing = store.getState().helpers.errorMsg;
    // clear previous error for this field first
    const cleared: Record<string, string> = {};
    for (const [k, v] of Object.entries(existing)) {
      if (k !== name) cleared[k] = String(v ?? "");
    }
    dispatch(setErrorMsg(cleared));

    // pattern check
    if (pattern && raw && !pattern.test(raw)) {
      dispatch(
        setErrorMsg({
          ...existing,
          [name]: patternMsg || `Invalid input. Please enter a valid ${name}`,
        })
      );
    }
  };

  /* ---- onBlur: required‑field validation ---- */
  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;

    // Pattern check takes first priority
    if (pattern && val && !pattern.test(val)) {
      dispatch(
        setErrorMsg({
          ...store.getState().helpers.errorMsg,
          [name]: patternMsg || `Invalid input. Please enter a valid ${name}`,
        })
      );
      return;
    }

    // Required check
    if (required && !val) {
      dispatch(
        setErrorMsg({ ...errorMsg, [name]: `${label} is required!.` })
      );
      return;
    }

    // Only call parent onBlur if no pattern error exists
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let val = e.target.value;
    
    // sanitize text type
    val = val.replace(TEXT_SANITIZE_RE, "");
    
    validate(val);
    onChange(name, val);
  };

  const field = (
    <div className="flex flex-col gap-1 w-full" role="group" data-slot="field" data-field-size={controlSize}>
      <Label htmlFor={name} className="text-sm font-medium mb-3 normal-case">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      <TextArea
        id={name}
        name={name}
        value={String(value ?? "")}
        disabled={disabled}
        required={required}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        autoFocus={autoFocus}
        className={error ? "!border-destructive focus:!ring-destructive" : ""}
      />
      {error && (
        <p className="text-xs text-destructive m-0">{error}</p>
      )}
    </div>
  );

  if (!tooltipText) return field;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{field}</TooltipTrigger>
        <TooltipContent side="top" align="end" sideOffset={-17} className="z-[1400]">
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};